import React from "react";
import { clone } from "../utils/CoreUtils";
import { getCurrentVersion } from "../utils/Versioning";
import Demo from "./Demo";
import { NotificationData } from "./Notification";
import { QuickbarData } from "./Quickbar";
import { Resource, ResourceData } from "./Resources";
import { BuildingData } from "./buildings/Buildings";
import { EventOccurrenceData } from "./events/Events";
import { EventTag } from "./events/GameEvent";
import { AttackTarget } from "./exploration/AttackTarget";
import { CombatStat } from "./exploration/CombatStats";
import { ExplorationStatus } from "./exploration/ExplorationStatus";
import { EquipmentSlot } from "./items/Equipment";
import { ItemOccurrence, ItemStack } from "./items/Item";
import { School, SpellElement } from "./spells/ElementsAndSchools";
import { TemporaryEffectData } from "./timetick/TemporaryEffects";

export type PlayerContextGlobalApprenticeLoadout = {
  name: string;
  allocation: Record<string, number>;
};

export type PlayerContextGlobalOptionsState = {
  targetFps: number;
  wakeLock: boolean;
  hiddenFromBottomMenu: string[];
  hideDamageEffects: boolean;
  doNotUseNativeDriver: boolean;
  overrideElementDisplay: SpellElement | "no";
  zoomLevel: number;
  allowAnalytics: boolean;
  themeMode: "light" | "dark";
  hideEventTags: EventTag[];
  automaticallyBuiltBuildingsStartOff: boolean;
  enableSwiping: boolean;
  enableBottomMenu: boolean;
  leftHandedMode: boolean;
  putCastSpellOnBottomMenu: boolean;
  hideTooltips: boolean;
  applyAllResearchers: boolean;
  applyAllAutocast: boolean;
  applyAllCreatures: boolean;
};

export type PlayerContextGlobalState = {
  flags: Record<string, boolean>;
  maxPrimarySchoolLevels: Partial<Record<School, number>>;
  maxSchoolLevels: Partial<Record<School, number>>;
  unlockedElements: Record<string, boolean>;
  unlockedSchools: Record<School, boolean>;
  researchersHiredLastRun: number;
  timeCapsulesLastRun: number;
  lastDungeonFloorCompletedLastRun: string | undefined;
  totalTimesResetted: number;
  endingsTriggered: string[];
  autocastLoadouts: (PlayerContextGlobalApprenticeLoadout | null)[];
  quickbars: QuickbarData[][];
  currentQuickbar: number;
  timesEnemyDefeated: Record<string, { player: number; creatures: number }>;
  newGamePlusBuffLevel: number;
  newGamePlusDifficultyLevel: number;
  thisWorldSecondsPlayed: number;
  thisWorldActiveSecondsPlayed: number;
  thisWorldStartTimestamp: number;
  // The above fields reset on NG+ - The below fields (and options) persist through NG+
  startTimestamp: number;
  secondsPlayed: number;
  activeSecondsPlayed: number | "untracked";
  appVersionTimestamp: number;
  appVersionString: string;
  isFullVersionSave: boolean;
  tickCount: number;
  newGamePlusCount: number;
  newGamePlusMax: number;
  notifications: NotificationData[];
} & PlayerContextGlobalOptionsState;

export type PlayerContextEquipmentState = Record<
  EquipmentSlot,
  ItemOccurrence | undefined
>;

export type PlayerContextEquipmentLoadout = {
  name: string;
  equipment: PlayerContextEquipmentState;
};

export type CombatActionResult = {
  id: string;
  damage: number;
  isHit: boolean;
  isCrit: boolean;
  time: number;
  target: AttackTarget;
  tags: string[];
};

export type MessageLog = {
  id: string;
  timestamp: number;
  realTimestamp?: number;
  message: string;
  accessibleMessage?: string;
};

export type EndingStats = {
  endingId: string;
  totalTimePlayed: number;
  totalTimePlayedThisWorld?: number;
  totalTimePlayedThisRun: number;
  totalActiveTimePlayed?: number;
  totalActivePlayedThisWorld?: number;
  totalRealWorldTimePlayed?: number;
  totalRealWorldTimePlayedThisWorld?: number;
  primarySchool: School;
  primaryElement: SpellElement;
  primarySchoolLevelOnEnding: number;
  mpl: number;
  researchMultiplier: number;
  productionMultiplier: number;
  completedStorylines: number;
  challengeId: string | undefined;
};

export type PlayerContextExplorationState = {
  currentHP: number;
  explorationStatus: ExplorationStatus;
  explorationStartTime: number;
  currentDungeonFloorId: string;
  currentEnemy: {
    id: string;
    currentHP: number;
  };
  currentSummon:
  | {
    id: string;
    currentHP: number;
  }
  | undefined;
  successfulExplorationsPerFloor: Record<string, number>;
  playerActionProgress: number;
  summonActionProgress: number;
  enemyActionProgress: number;
  lastPlayerActionResult: CombatActionResult | undefined;
  lastEnemyActionResult: CombatActionResult | undefined;
  lastPlayerHitCause: string | undefined;
  actionResultQueue: CombatActionResult[];
  variables: Record<string, boolean>;
};

export type PlayerContextState = {
  resources: Partial<Record<Resource, ResourceData>>;
  buildings: Record<string, BuildingData>;
  flags: Record<string, boolean>;
  elementExperience: Record<string, number>;
  schoolExperience: Partial<Record<School, number>>;
  schoolExponents: Partial<Record<School, number>>;
  research: {
    totalResearchers: number;
    researcherAllocation: {
      schools: Partial<Record<School, number>>;
    };
  };
  autocast: {
    apprenticeAllocation: Record<string, number>;
    spellAutocastProgress: Record<string, number>;
    apprenticesPaused: boolean;
    loadouts: Record<string, Record<string, number>>;
    lastLoadoutUsed: number;
  };
  creatures: {
    totalCreatures: number;
    creatureAllocation: Record<string, number>;
    lastRewardTime: Record<string, number>;
  };
  eventQueue: string[];
  eventOccurrences: Record<string, EventOccurrenceData>;
  pityEnabledAtSeconds: number | undefined;
  pityCounter: number;
  nextEventIsManuallyTriggered: boolean;
  global: PlayerContextGlobalState;
  thisRun: {
    secondsPlayed: number;
    activeSecondsPlayed: number | "untracked";
    newFlagsOnRetirement: Record<string, boolean>;
    endingTriggered: EndingStats | undefined;
    startTimestamp: number;
    lastDungeonFloorCompleted: string | undefined;
  };
  temporaryEffects: Record<string, TemporaryEffectData | number>;
  recentSpellsCast: string[];
  nextRandomEvent: number;
  primaryElement: string | undefined;
  primarySchool: School | undefined;
  inventory: ItemStack[];
  equipment: PlayerContextEquipmentState;
  equipmentLoadouts: PlayerContextEquipmentLoadout[];
  lastEquipmentLoadoutModified: number;
  exploration: PlayerContextExplorationState;
  explorationMessageLog: MessageLog[];
  creatureMessageLog: MessageLog[];
  lastSpellCastTimes: Record<string, number>;
  uiCommands: string[];
  lastProcessedTime: number;
  lastItemUsedTimes: Record<string, number>;
  quickbar: QuickbarData[];
  favoriteSpells: string[];
  skipAheadTime: number;
  warpTimeSecs: number;
  summoningBlockedUntilTime: number;
  strengtheningsBought: Record<CombatStat, number>;
  currentChallengeId: string | undefined;
  isFake: boolean;
};

export type PlayerContextTransform = (
  oldState: PlayerContextState,
) => PlayerContextState;

export type PlayerContextValue = PlayerContextState & {
  apply: (transform: PlayerContextTransform) => void;
};

export const EXPLORATION_INITIAL_STATE: PlayerContextExplorationState = {
  currentHP: 100,
  explorationStatus: ExplorationStatus.None,
  explorationStartTime: 0,
  currentDungeonFloorId: "",
  currentEnemy: {
    id: "",
    currentHP: 1,
  },
  currentSummon: undefined,
  successfulExplorationsPerFloor: {},
  playerActionProgress: 0.0,
  summonActionProgress: 0.0,
  enemyActionProgress: 0.0,
  lastPlayerActionResult: undefined,
  lastEnemyActionResult: undefined,
  lastPlayerHitCause: undefined,
  actionResultQueue: [],
  variables: {},
};

export const EQUIPMENT_INITIAL_STATE: PlayerContextEquipmentState = {
  Hand: undefined,
  Body: undefined,
  Accessory: undefined,
};

export const GLOBAL_INITIAL_STATE: PlayerContextGlobalState = {
  secondsPlayed: 0,
  activeSecondsPlayed: 0,
  flags: {},
  maxPrimarySchoolLevels: {},
  maxSchoolLevels: {},
  unlockedElements: { Fire: true, Earth: true, Water: true, Air: true },
  unlockedSchools: {
    Conjuration: true,
    Enchantment: true,
    Illusion: true,
    Alchemy: false,
    Evocation: false,
    Protection: false,
    Breeding: false,
    Summoning: false,
    Divination: false,
  },
  researchersHiredLastRun: 0,
  timeCapsulesLastRun: 0,
  lastDungeonFloorCompletedLastRun: undefined,
  totalTimesResetted: 0,
  targetFps: 30,
  endingsTriggered: [],
  startTimestamp: Date.now(),
  allowAnalytics: false,
  appVersionTimestamp: 0,
  appVersionString: getCurrentVersion() ?? "0.0.0",
  isFullVersionSave: !Demo.isDemo(),
  themeMode: "light",
  hideEventTags: [],
  automaticallyBuiltBuildingsStartOff: false,
  enableSwiping: true,
  enableBottomMenu: true,
  leftHandedMode: false,
  putCastSpellOnBottomMenu: true,
  autocastLoadouts: [],
  quickbars: [],
  currentQuickbar: 0,
  tickCount: 0,
  wakeLock: false,
  hiddenFromBottomMenu: [],
  hideDamageEffects: false,
  doNotUseNativeDriver: false,
  timesEnemyDefeated: {},
  overrideElementDisplay: "no",
  zoomLevel: 1.0,
  hideTooltips: false,
  newGamePlusCount: 0,
  newGamePlusMax: 0,
  newGamePlusBuffLevel: 0,
  newGamePlusDifficultyLevel: 0,
  thisWorldSecondsPlayed: 0,
  thisWorldActiveSecondsPlayed: 0,
  thisWorldStartTimestamp: 0,
  notifications: [],
  applyAllAutocast: false,
  applyAllCreatures: false,
  applyAllResearchers: false
};

export const THIS_RUN_INITIAL_STATE = {
  secondsPlayed: 0,
  activeSecondsPlayed: 0,
  newFlagsOnRetirement: {},
  endingTriggered: undefined,
  startTimestamp: Date.now(),
  lastDungeonFloorCompleted: undefined,
};

export const PLAYER_INITIAL_STATE: PlayerContextState = {
  resources: {},
  buildings: {},
  flags: {},
  elementExperience: {
    Fire: 0,
    Earth: 0,
    Water: 0,
    Air: 0,
  },
  schoolExperience: {
    Conjuration: 0,
    Enchantment: 0,
    Illusion: 0,
  },
  schoolExponents: {},
  research: {
    totalResearchers: 0,
    researcherAllocation: {
      schools: {},
    },
  },
  autocast: {
    apprenticeAllocation: {},
    spellAutocastProgress: {},
    apprenticesPaused: false,
    loadouts: {},
    lastLoadoutUsed: 0,
  },
  creatures: {
    totalCreatures: 0,
    creatureAllocation: {},
    lastRewardTime: {},
  },
  eventQueue: [],
  eventOccurrences: {},
  pityEnabledAtSeconds: undefined,
  pityCounter: 0,
  nextEventIsManuallyTriggered: false,
  global: GLOBAL_INITIAL_STATE,
  thisRun: THIS_RUN_INITIAL_STATE,
  temporaryEffects: {},
  recentSpellsCast: [],
  nextRandomEvent: 600,
  primaryElement: undefined,
  primarySchool: undefined,
  inventory: [],
  equipment: EQUIPMENT_INITIAL_STATE,
  exploration: EXPLORATION_INITIAL_STATE,
  explorationMessageLog: [],
  creatureMessageLog: [],
  lastSpellCastTimes: {},
  uiCommands: [],
  lastProcessedTime: Date.now(),
  lastItemUsedTimes: {},
  quickbar: [],
  favoriteSpells: [],
  skipAheadTime: 0,
  warpTimeSecs: 0,
  summoningBlockedUntilTime: 0,
  strengtheningsBought: {
    MaxHP: 0,
    Attack: 0,
    AttackDelay: 0,
    Defense: 0,
    HPRegen: 0,
    Dodge: 0,
    Accuracy: 0,
    CritChance: 0,
  },
  equipmentLoadouts: [],
  lastEquipmentLoadoutModified: 0,
  currentChallengeId: undefined,
  isFake: false
};

export const PlayerContext = React.createContext({
  ...clone(PLAYER_INITIAL_STATE),
  apply: (transform: PlayerContextTransform) => { },
  overwrite: (transform: PlayerContextTransform) => { },
});
